import cardService from '@/services/card.service'
import CookieService from '@/services/cookie.service'
import { store } from '@/store/store'
import { CardListType, RecCardType } from '@/types/page'
import { CardType } from '@/types/page'
import { Mode } from '@/utils/constants'
import { sortByModes } from '@/utils/constants'
import { cardListsToNames } from '@/utils/helpers'

import { SET_STATE } from './types'

type sortProps = {
  cardLists: CardListType[]
  cardMap: Record<string, CardType>
  mode: (typeof sortByModes)[number]['key']
}

export const sort = ({ cardLists, cardMap, mode }: sortProps) => {
  switch (mode) {
    case 'percent':
      return cardLists.map((cardList) => ({
        ...cardList,
        cardviews: [...cardList.cardviews].sort(
          (a, b) => b.num_decks / b.potential_decks - a.num_decks / a.potential_decks,
        ),
      }))
    case 'count':
      return cardLists.map((cardList) => ({
        ...cardList,
        cardviews: [...cardList.cardviews].sort((a, b) => b.num_decks - a.num_decks),
      }))
    case 'synergy':
      return cardLists.map((cardList) => ({
        ...cardList,
        cardviews: [...cardList.cardviews].sort((a, b) => b.synergy - a.synergy),
      }))
    case 'name':
      return cardLists.map((cardList) => ({
        ...cardList,
        cardviews: [...cardList.cardviews].sort((a, b) => a.name.localeCompare(b.name)),
      }))
    case 'mana':
      return cardLists.map((cardList) => ({
        ...cardList,
        cardviews: [...cardList.cardviews].sort((a, b) => cardMap[a.sanitized_wo]?.cmc - cardMap[b.sanitized_wo]?.cmc),
      }))
    case 'price':
      return cardLists.map((cardList) => ({
        ...cardList,
        cardviews: [...cardList.cardviews].sort(
          (a, b) =>
            (cardMap[a.cards ? a.cards[0].url : a.sanitized_wo]?.prices.tcgplayer?.price || Infinity) -
            (cardMap[b.cards ? b.cards[0].url : b.sanitized_wo]?.prices.tcgplayer?.price || Infinity),
        ),
      }))
    case 'salt':
      return cardLists.map((cardList) => {
        return {
          ...cardList,
          cardviews: [...cardList.cardviews].sort(
            (a, b) => cardMap[b.sanitized_wo]?.salt - cardMap[a.sanitized_wo]?.salt,
          ),
        }
      })
    default:
      throw new Error(`Unknown sort mode: ${mode}`)
  }
}

// Alters the mode if the current mode doesn't apply to the current cardviews
const fixMode = (mode: string, cardviews: RecCardType[]) => {
  const countModeAvailable = cardviews.some((cardview) => cardview.num_decks)
  if (
    (mode === 'count' && !countModeAvailable) ||
    (mode === 'percent' && !cardviews.some((cardview) => cardview.potential_decks)) ||
    (mode === 'synergy' && !cardviews.some((cardview) => cardview.synergy))
  ) {
    if (countModeAvailable) {
      return 'count'
    } else {
      return 'name'
    }
  }
  return mode
}

export const sortBy = async (mode: string) => {
  const dispatch = store.dispatch
  const { cardLists } = store.getState().cardlists
  const { skipFetch } = sortByModes.find((m) => m.key === mode) as Mode

  dispatch({ type: SET_STATE, payload: { loading: true } })
  CookieService.set('sortedBy', mode)

  mode = fixMode(
    mode,
    cardLists.flatMap((cardList) => cardList.cardviews),
  )

  switch (mode) {
    case 'percent': {
      const modeAvailable = cardLists
        .flatMap((cardList) => cardList.cardviews)
        .some((cardview) => cardview.potential_decks)
      if (!modeAvailable) {
        mode = 'count'
      }
    }
    case 'count': {
      const modeAvailable = cardLists.flatMap((cardList) => cardList.cardviews).some((cardview) => cardview.num_decks)
      if (!modeAvailable) {
        mode = 'name'
      }
    }
    case 'synergy': {
      const modeAvailable = cardLists.flatMap((cardList) => cardList.cardviews).some((cardview) => cardview.synergy)
      if (!modeAvailable) {
        mode = 'count'
      }
    }
  }

  // Fetch full card data
  // Always fetches, but only awaits if data is needed now
  let cardMap = {}
  if (!skipFetch) {
    cardMap = await cardService.getCardMap(cardListsToNames(cardLists))
  }

  // Sort cards
  const newCardLists = sort({ cardLists, cardMap, mode })

  dispatch({
    type: SET_STATE,
    payload: {
      cardLists: newCardLists,
      loading: false,
      sortedBy: mode,
      updatedAt: Date.now(),
    },
  })
}
